import { useEffect, useState } from 'react';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterCommodity, DemeterDataFrequency, DemeterPageType, DemeterRegion, DemeterTableDefinitionType } from '../../../Generated/Raven-Demeter/api';
import useCommentaryApi from '../../Apis/Hooks/useCommentaryApiHook';
import AggregateRegionSelector from '../../Components/AggregateRegionSelector/AggregateRegionSelector';
import ProjectionChart from '../../Components/Charts/Projection/ProjectionChart';
import SeasonalChart from '../../Components/Charts/Seasonal/SeasonalChart';
import OriginsAndDestinationsContainer from '../../Components/Containers/OriginsAndDestinations/OriginsAndDestinationsContainer';
import UnitOfMeasureMetricImperialDropdown from '../../Components/Form/Inputs/UnitOfMeasureMetricImperialDropdown';
import TutorialLink from '../../Components/Form/Links/TutorialLink/TutorialLink';
import PageHeaderWithSelections from '../../Components/Headers/PageHeaderWithSelections';
import useTableDefinitionNavigation from '../../Components/Navigation/Hooks/useTableDefinitionNavigationHook';
import SeasonalValueAndPercentTables from '../../Components/Tables/Seasonal/SeasonalValueAndPercentTables';
import useMarketByCommodity from '../../Core/Hooks/useMarketByCommodity';
import useUnitOfMeasure from '../../Core/Hooks/useUnitOfMeasureHook';
import { isRegionCommodityGroupedMarket } from '../../Core/MarketDefinitions';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './ImportsPage.module.scss';

const ImportsPage: React.FC = () => {
    // Constants.
    const [translations] = useLanguage();
    const pageTitle = translations.imports.title;
    const tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyImportsTable;

    // Hooks.
    const [, tableDefinitions, regionCommoditySelection] = useTableDefinitionNavigation(tableDefinitionType);
    const commentary = useCommentaryApi(tableDefinitions?.demeterCommentaryGuid);
    const [unitOfMeasure, setUnitOfMeasure] = useUnitOfMeasure('MetricImperial');
    const marketByCommodity = useMarketByCommodity(tableDefinitionType);
    const [aggregateRegions, setAggregateRegions] = useState<DemeterRegion[]>([]);

    const isYearlyOnly = tableDefinitions?.dataFrequencies?.length === 1 && tableDefinitions.dataFrequencies[0] === DemeterDataFrequency.Yearly;
    const showAllChartsAndTable = isRegionCommodityGroupedMarket(marketByCommodity);
    const showOriginsChartAndTable = !(
        [regionCommoditySelection?.commodity, tableDefinitions?.topLevelGroup].includes(DemeterCommodity.Butter) &&
        regionCommoditySelection?.region === DemeterRegion.UnitedStates
    );

    useEffect(() => {
        setAggregateRegions([]);
    }, [regionCommoditySelection?.region]);

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithSelections
                title={pageTitle}
                tableDefinitionType={tableDefinitionType}
                rightSideOfHeaderComponent={
                    <>
                        <UnitOfMeasureMetricImperialDropdown
                            unitOfMeasure={unitOfMeasure}
                            handleChange={setUnitOfMeasure}
                            track={{ eventCategory: 'Imports', eventAction: 'UnitOfMeasureChanged', eventValue: unitOfMeasure }}
                        />
                        <TutorialLink link={applicationConstants.TutorialLinks.fundamentalsSupplyDemand} />
                    </>
                }
                testId="ImportsPageHeader"
                track={{ eventCategory: 'Imports', eventAction: 'PageHeaderDisplayed' }}
            />
            {showAllChartsAndTable && regionCommoditySelection && (
                <>
                    <div className={styles.imports_main_container}>
                        <div className={styles.imports_projection_chart}>
                            <ProjectionChart
                                title={pageTitle}
                                tableDefinitionType={tableDefinitionType}
                                regionCommoditySelection={regionCommoditySelection!}
                                aggregateRegions={aggregateRegions}
                                unitOfMeasure={unitOfMeasure}
                                pageType={DemeterPageType.Imports}
                                testId="ImportsProjectionChart"
                                track={{
                                    eventCategory: 'Imports',
                                    eventAction: 'ProjectionChart',
                                    eventValue: { tableDefinitionType, regionCommoditySelection },
                                }}
                            />
                        </div>

                        {regionCommoditySelection?.region === DemeterRegion.TopThirty ? (
                            <div className={styles.imports_commentary_container}>
                                <AggregateRegionSelector
                                    title={pageTitle}
                                    groupByParentRegions
                                    tableDefinitionType={tableDefinitionType}
                                    regionCommoditySelection={regionCommoditySelection!}
                                    handleRegionSelections={setAggregateRegions}
                                    track={{
                                        eventCategory: 'Imports',
                                        eventAction: 'AggregatedRegionSelector',
                                    }}
                                />
                            </div>
                        ) : (
                            tableDefinitions?.demeterCommentaryGuid && (
                                <div className={styles.imports_commentary_container}>
                                    <p>{commentary}</p>
                                </div>
                            )
                        )}
                    </div>
                    {showOriginsChartAndTable && (
                        <OriginsAndDestinationsContainer
                            tableDefinitionType={tableDefinitionType}
                            regionCommoditySelection={regionCommoditySelection!}
                            unitOfMeasure={unitOfMeasure}
                            testId="ImportsOriginsAndDestinations"
                            track={{
                                eventCategory: 'Imports',
                                eventAction: 'OriginsAndDestinations',
                                eventValue: { tableDefinitionType, regionCommoditySelection, unitOfMeasure },
                            }}
                        />
                    )}
                </>
            )}
            {!isYearlyOnly && regionCommoditySelection && (
                <div className={styles.imports_main_container}>
                    <div className={styles.imports_seasonal_chart}>
                        <SeasonalChart
                            title={pageTitle}
                            tableDefinitionType={tableDefinitionType}
                            regionCommoditySelection={regionCommoditySelection!}
                            aggregateRegions={aggregateRegions}
                            unitOfMeasure={unitOfMeasure}
                            pageType={DemeterPageType.Imports}
                            testId="ImportsSeasonalChart"
                            track={{
                                eventCategory: 'Imports',
                                eventAction: 'SeasonalChart',
                                eventValue: { tableDefinitionType, regionCommoditySelection },
                            }}
                        />
                    </div>
                </div>
            )}
            {showAllChartsAndTable && !isYearlyOnly && regionCommoditySelection && (
                <SeasonalValueAndPercentTables
                    title={pageTitle}
                    tableDefinitionType={tableDefinitionType}
                    regionCommoditySelection={regionCommoditySelection!}
                    aggregateRegions={aggregateRegions}
                    unitOfMeasure={unitOfMeasure}
                    testId="ImportsSeasonalValueAndPercentTables"
                    track={{
                        eventCategory: 'Imports',
                        eventAction: 'SeasonalValueAndPercentTables',
                        eventValue: { tableDefinitionType, regionCommoditySelection },
                    }}
                />
            )}
        </div>
    );
};
export default ImportsPage;
